import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Building2, 
  Dumbbell, 
  Sparkles, 
  Hotel, 
  Briefcase,
  ArrowRight,
  Calendar,
  CreditCard,
  Thermometer,
  DoorOpen,
  Clock,
  Zap,
  Shield,
  TrendingUp,
  Users,
  CheckCircle2,
  Play
} from "lucide-react";
import wellsuitesLogoBlack from "@/assets/wellsuites-logo-black.png";
import saunaHero from "@/assets/sauna-hero.jpg";
import SEO from "@/components/SEO";

const ForBusiness = () => {
  const targetAudience = [
    { icon: Sparkles, label: "Recovery Studios" },
    { icon: Dumbbell, label: "Gyms" },
    { icon: Building2, label: "Spas" },
    { icon: Hotel, label: "Hotels" },
    { icon: Briefcase, label: "Corporate Wellness" },
  ];

  const coreFeatures = [
    {
      icon: Calendar,
      title: "White Labelled Booking Platform",
      description: "Custom domain, branded experience, no Wellsuites branding visible to your customers"
    },
    {
      icon: CreditCard,
      title: "Bookings & Memberships",
      description: "One-off sessions, memberships, and member discounts all in one place"
    },
    {
      icon: Users,
      title: "Self-Service Access",
      description: "Customers book and access suites without staff involvement"
    },
    {
      icon: DoorOpen,
      title: "Cloud Issued Door Access",
      description: "Access codes issued automatically and expire when the session ends"
    },
    {
      icon: Clock,
      title: "Overstay Penalties",
      description: "Automatic penalties to protect schedules and revenue"
    },
    {
      icon: Thermometer,
      title: "Sauna & Suite Automation",
      description: "Saunas pre-heat automatically before sessions, no staff interaction required"
    },
  ];

  const operatorBenefits = [
    "Reduce staffing requirements",
    "Increase utilisation and revenue",
    "Deliver consistently warm, ready saunas",
    "Prevent overstays and misuse",
    "Keep costs predictable while scaling",
  ];

  const flowSteps = ["Book", "Pay", "Suite Prepared", "Access Issued", "Session Ends", "Access Expires"];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Wellsuites for Business — Smart wellness booking & access"
        description="Run your sauna, cold plunge, or recovery studio with Wellsuites. Online booking, smart locks, payments, and member management in one platform."
        canonical="https://wellsuites.co/for-business"
      />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src={wellsuitesLogoBlack} 
                alt="WellSuites" 
                className="h-6 w-auto"
              />
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/contact">
                <Button variant="ghost" size="sm" className="text-gray-600 hover:text-gray-900">
                  Talk to Sales
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="sm" className="bg-gray-900 hover:bg-gray-800 text-white">
                  Book a Demo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-5xl font-bold text-gray-900 tracking-tight leading-tight mb-6">
                All-in-one booking, access, and automation for recovery and wellness suites
              </h1>
              <p className="text-xl text-gray-600 mb-10">
                Wellsuites powers bookings, memberships, payments, door access, and suite automation — fully white labelled under your brand.
              </p>
              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4">
                <Link to="/contact">
                  <Button size="lg" className="bg-gray-900 hover:bg-gray-800 text-white px-8 h-12 text-base">
                    Book a Demo
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50 px-8 h-12 text-base">
                    Talk to Sales
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src={saunaHero} 
                alt="Person relaxing in a modern sauna" 
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-semibold text-gray-900 text-center mb-10">
            Built for recovery and wellness operators
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {targetAudience.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="flex items-center gap-3 bg-white px-5 py-3 rounded-full shadow-sm border border-gray-100">
                  <Icon className="h-5 w-5 text-gray-700" />
                  <span className="text-gray-800 font-medium">{item.label}</span>
                </div>
              );
            })}
          </div>
          <p className="text-center text-gray-500 mt-6 text-sm">
            Suitable for single site and multi-location operators
          </p>
        </div>
      </section>

      {/* What Wellsuites Does */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            More than booking software
          </h2>
          <p className="text-lg text-gray-600 mb-12">
            Wellsuites is a complete operating platform that handles everything from the moment a customer books to when they leave — automatically.
          </p>
          
          {/* Flow Diagram */}
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-4">
            {flowSteps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium">
                  {step}
                </div>
                {index < flowSteps.length - 1 && (
                  <ArrowRight className="h-4 w-4 text-gray-400 mx-1 hidden md:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Everything you need in one platform
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coreFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-0 shadow-sm bg-white">
                  <CardContent className="p-6">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                      <Icon className="h-5 w-5 text-gray-700" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sauna Pre-Heating - Key Differentiator */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
                <Thermometer className="h-4 w-4" />
                Key Differentiator
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Saunas ready when your customer arrives
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Wellsuites is the only solution on the market that integrates booking data with sauna control to automatically pre-heat saunas before a session starts.
              </p>
              <p className="text-gray-600 mb-8 leading-relaxed">
                This eliminates the common industry problem where customers must turn on the sauna themselves on arrival, resulting in a cold start and poor experience.
              </p>
              <ul className="space-y-3">
                {[
                  "Sauna pre-heats automatically before the session",
                  "No staff involvement required",
                  "Consistent, premium customer experience",
                  "Eliminates complaints and dissatisfaction",
                  "Designed specifically for timed wellness sessions"
                ].map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 flex items-center justify-center min-h-[300px]">
              <div className="text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Thermometer className="h-10 w-10 text-orange-500" />
                </div>
                <p className="text-2xl font-bold text-gray-900">75°C</p>
                <p className="text-gray-600 text-sm">Ready on arrival</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Affordable Access Control */}
      <section className="py-20 bg-gray-900 text-white px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Access control that doesn't break the bank
              </h2>
              <p className="text-gray-300 mb-8 leading-relaxed">
                Traditional access control systems typically cost £1,500+ per door, require specialist installers, and are built for offices rather than customer sessions.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Low cost setup", icon: TrendingUp },
                  { label: "No specialist installation", icon: Zap },
                  { label: "Booking & access integrated", icon: Calendar },
                  { label: "Built for wellness", icon: Shield }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                      <Icon className="h-5 w-5 text-white/80" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="text-center">
              <div className="bg-white/10 rounded-2xl p-8">
                <p className="text-gray-400 text-sm mb-2">Traditional systems</p>
                <p className="text-3xl font-bold text-gray-400 line-through mb-6">£1,500+ per door</p>
                <div className="border-t border-white/20 pt-6">
                  <p className="text-gray-300 text-sm mb-2">Wellsuites</p>
                  <p className="text-4xl font-bold text-white">Under £400</p>
                  <p className="text-gray-400 text-sm mt-2">per door, fully integrated</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live in One Day */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium mb-4">
            <Zap className="h-4 w-4" />
            Fast Setup
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Live within one working day
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-2xl mx-auto">
            Wellsuites can be fully configured and integrated within a single day. No long installs, no disruption.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              "Booking system",
              "Memberships & pricing",
              "Door access",
              "Sauna automation",
              "White labelled experience"
            ].map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-800">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits for Operators */}
      <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Benefits for operators
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {operatorBenefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-4 bg-white rounded-xl p-5 shadow-sm">
                <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="h-4 w-4 text-white" />
                </div>
                <span className="text-gray-800 font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Run your recovery suites smarter
          </h2>
          <p className="text-gray-300 mb-10 text-lg">
            Professional grade booking, access control, and suite automation without enterprise level costs.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 px-8 h-12 text-base font-medium">
                Book a Demo
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 px-8 h-12 text-base font-medium">
                Speak to Our Team
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img 
              src={wellsuitesLogoBlack} 
              alt="WellSuites" 
              className="h-5 w-auto opacity-60"
            />
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <Link to="/about" className="hover:text-gray-900 transition-colors">About</Link>
              <Link to="/contact" className="hover:text-gray-900 transition-colors">Contact</Link>
              <Link to="/privacy" className="hover:text-gray-900 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-gray-900 transition-colors">Terms</Link>
            </div>
          </div>
          <div className="text-center mt-8 text-sm text-gray-400">
            &copy; 2025 WellSuites. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ForBusiness;
